/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// JSX rewrite of the prototype's render function: the stats bar
// (core-viewer.js renderStats, 4398-4461) as JSX. Branch logic verbatim;
// template strings became JSX; the escapeHtml calls are gone (React escapes).
// In the prototype renderStats also chained into renderMurmurEvidence()/
// renderPatternResults() and imperatively updated waveMeta — those are sibling
// components / wrapper chrome now, so this component renders exactly the three
// stats-bar states the prototype shows:
//   1. gate hard-rejected  -> gate warning + Not reportable + duration
//   2. no S1/S2 peaks      -> raw-sample review mode notice (+ limited-gate
//                             notice when the gate is `limited`)
//   3. normal              -> hidden (the prototype hid the bar too: the
//                             evidence and results bars carry the content)
// Verified against the source: the normal path's computed values (cycle/sys/
// dia/ratio, marker counts, timingGuideHover, the limited-gate notice) are DEAD
// in the extracted prototype — computed, then discarded by
// `classList.add('hidden'); innerHTML = ''` (4448-4450). The `secondary` class
// is only ever removed, never added (4404). This component is therefore the
// COMPLETE stats bar; the gate-limited metrics/reasons display lives in the
// PcgRejectGatePanel (rejectGateDetailsHtml), not here.

import React, { ReactElement } from "react"
import { isGateHardRejected } from "../core/gate"
import { PcgCore } from "../core"

/** The gate fields this bar reads; the full payload stays loose-typed. */
type GateSummary = {
  status?: string
  reason?: string
  clinician_message?: string
} | null

const GateNotice = ({ gate }: { gate: GateSummary }): ReactElement | null => {
  if (gate && isGateHardRejected(gate)) {
    return (
      <div className="gate-warning">
        <span className="gate-title">File quality insufficient for clinical analysis.</span>
        <span className="gate-detail">
          This file was rejected before report generation. {gate.reason || gate.clinician_message || ""}
        </span>
      </div>
    )
  }
  if (gate?.status === "limited") {
    return (
      <div className="gate-limited">
        <span className="gate-title">File accepted with limited confidence.</span>
        <span className="gate-detail">{gate.reason || gate.clinician_message || ""}</span>
      </div>
    )
  }
  return null
}

const PcgStatsBar = ({ core }: { core: PcgCore }): ReactElement | null => {
  const D = core.ctx.payload
  const gate = (D.cardiacPlausibilityGate || D.examPackage?.cardiac_plausibility_gate || null) as GateSummary

  if (gate && isGateHardRejected(gate)) {
    return (
      <div className="stats-bar">
        <GateNotice gate={gate} />
        <div className="stat">
          <div className="stat-value">Not reportable</div>
          <div className="stat-label">Reject Gate</div>
        </div>
        <div className="stat">
          <div className="stat-value">
            {D.duration ? D.duration.toFixed(1) : "-"} <span className="stat-unit">s</span>
          </div>
          <div className="stat-label">Duration</div>
        </div>
      </div>
    )
  }
  if (!D.peaks || D.peaks.length === 0) {
    return (
      <div className="stats-bar">
        <GateNotice gate={gate} />
        <div className="stat">
          <div className="stat-value">Raw sample</div>
          <div className="stat-label">Review mode</div>
        </div>
        <div className="stat">
          <div className="stat-value">No S1/S2 guides available</div>
          <div className="stat-label">Guide layer</div>
        </div>
        <div className="stat">
          <div className="stat-value">
            {D.duration.toFixed(1)} <span className="stat-unit">s</span>
          </div>
          <div className="stat-label">Duration</div>
        </div>
      </div>
    )
  }
  // Normal path: prototype hides the stats bar entirely (classList.add('hidden'),
  // innerHTML = '') — the evidence and results bars carry the content. Note the
  // prototype computes the limited-gate notice (rejectGate) here but only ever
  // shows it in the two branches above; that quirk is preserved (fixture-pinned:
  // gate-limited recordings with peaks render nothing here).
  return null
}

export default PcgStatsBar
